import React, { useEffect, useState } from 'react';
import { Alert, Card, Col, Container, Nav, Row, Tab } from 'react-bootstrap';
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaHourglassHalf,
  FaSpinner
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import DuserAuditProgress from '../../../Components/DuserAuditProgress';

function InDuserDashboard() {
  const [dashboardStats, setDashboardStats] = useState(null);
  const [reauditStats, setReauditStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const navigate = useNavigate();

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem('dUser'));
  const BranchCode = userData?.BranchCode;
  const department = userData?.department;
  const userName = userData?.firstName;
  const userId = userData?.id;

  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081';

  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  useEffect(() => {
    if (BranchCode && department && userId) {
      fetchDashboardStats();
      fetchUserProfile();
    } else {
      setError("Department, branch, or user information missing");
      setLoading(false);
    }
  }, [BranchCode, department, userId]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);

      // Fetch normal audit stats
      const normalResponse = await fetch(
        `${BASE_URL}/get-department-dashboard-stats/${BranchCode}/${department}`
      );

      // Fetch re-audit stats
      const reauditResponse = await fetch(
        `${BASE_URL}/get-department-reaudit-dashboard-stats/${BranchCode}/${department}`
      );

      if (!normalResponse.ok || !reauditResponse.ok) {
        throw new Error('Failed to fetch dashboard statistics');
      }

      const normalData = await normalResponse.json();
      const reauditData = await reauditResponse.json();

      if (normalData.success) {
        setDashboardStats(normalData.stats);
      } else {
        console.error("Failed to fetch normal audit stats:", normalData.error);
      }

      if (reauditData.success) {
        setReauditStats(reauditData.stats);
      } else {
        console.error("Failed to fetch re-audit stats:", reauditData.error);
      }

    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${BASE_URL}/get-department-user/${userId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const data = await response.json();

      if (data.success) {
        setUserProfile(data.user);
      } else {
        console.error("Failed to fetch user profile:", data.error);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const handleCardClick = (status, type = 'normal') => {
    // Navigate to the appropriate audit details page with the status as query parameter
    if (type === 'reaudit') {
      navigate(`/InDuserReAudit?tab=${status}`);
    } else {
      navigate(`/InDuserAudit?tab=${status}`);
    }
  };

  const getDepartmentInitials = () => {
    if (!userName) return "DU";
    return userName.split(' ').map(word => word.charAt(0)).join('').toUpperCase().substring(0, 2);
  };

  return (
    <Container fluid className="dashboard-container p-0">
      {/* Integrated header content without the styled header section */}
      <Container fluid className="px-4 pt-4 pb-2">
        <Row className="align-items-center mb-3">
          <Col md={6} className="mb-3 mb-md-0">
            <div className="d-flex align-items-center">
              {userProfile?.profileImage ? (
                <div className="admin-avatar-simple me-3">
                  <img 
                    src={`${BASE_URL}${userProfile.profileImage}`}
                    alt={`${userName || 'User'} Avatar`}
                    className="profile-image"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="department-avatar-fallback" style={{ display: 'none' }}>
                    {getDepartmentInitials()}
                  </div>
                </div>
              ) : (
                <div className="admin-avatar-simple me-3">
                  {getDepartmentInitials()}
                </div>
              )}
              <div>
                <h4 className="greeting mb-0 text-primary">Welcome {userName || 'Department User'}</h4>
                <p className="mb-0 text-muted">
                  <FaCalendarAlt className="me-1" size={14} /> {currentDate}
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      
      <Container fluid className="dashboard-content px-4 pb-3">
        {error && (
          <Alert variant="danger" className="mb-3">
            <Alert.Heading>Error Loading Dashboard</Alert.Heading>
            <p className="mb-0">{error}</p>
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-grow text-primary mb-3" style={{ width: '2.5rem', height: '2.5rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-primary fw-bold mb-1">Loading Dashboard</p>
            <p className="text-muted">Please wait while we fetch your stats...</p>
          </div>
        ) : (
          <div className="dashboard-grid">
            <div className="dashboard-section status-section">
              <Tab.Container id="status-tabs" defaultActiveKey="audits">
                <Nav variant="tabs" className="status-tabs mb-3">
                  <Nav.Item>
                    <Nav.Link eventKey="audits" className="status-tab">Audits</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="caps" className="status-tab">Corrective Action Plans</Nav.Link>
                  </Nav.Item>
                </Nav>
                <Tab.Content>
                  <Tab.Pane eventKey="audits">
                    {dashboardStats && (
                      <div className="status-cards">
                        <Card className="status-card" onClick={() => handleCardClick('pending', 'normal')}>
                          <Card.Body>
                            <div className="status-icon pending-icon">
                              <FaHourglassHalf />
                            </div>
                            <div className="status-content">
                              <h4 className="status-value">{dashboardStats.pending}</h4>
                              <p className="status-label">Plan</p>
                            </div>
                          </Card.Body>
                        </Card>
                        <Card className="status-card" onClick={() => handleCardClick('inProgress', 'normal')}>
                          <Card.Body>
                            <div className="status-icon progress-icon">
                              <FaSpinner />
                            </div>
                            <div className="status-content">
                              <h4 className="status-value">{dashboardStats.inProgress}</h4>
                              <p className="status-label">In Progress</p>
                            </div>
                          </Card.Body>
                        </Card>
                        <Card className="status-card" onClick={() => handleCardClick('completed', 'normal')}>
                          <Card.Body>
                            <div className="status-icon complete-icon">
                              <FaCheckCircle />
                            </div>
                            <div className="status-content">
                              <h4 className="status-value">{dashboardStats.completed}</h4>
                              <p className="status-label">Completed</p>
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                    )}
                  </Tab.Pane>
                  <Tab.Pane eventKey="caps">
                    {reauditStats && (
                      <div className="status-cards">
                        <Card className="status-card" onClick={() => handleCardClick('pending', 'reaudit')}>
                          <Card.Body>
                            <div className="status-icon cap-pending-icon">
                              <FaHourglassHalf />
                            </div>
                            <div className="status-content">
                              <h4 className="status-value">{reauditStats.pending}</h4>
                              <p className="status-label">Pending</p>
                            </div>
                          </Card.Body>
                        </Card>
                        <Card className="status-card" onClick={() => handleCardClick('inProgress', 'reaudit')}>
                          <Card.Body>
                            <div className="status-icon cap-progress-icon">
                              <FaSpinner />
                            </div>
                            <div className="status-content">
                              <h4 className="status-value">{reauditStats.inProgress}</h4>
                              <p className="status-label">In Progress</p>
                            </div>
                          </Card.Body>
                        </Card>
                        <Card className="status-card" onClick={() => handleCardClick('completed', 'reaudit')}>
                          <Card.Body>
                            <div className="status-icon cap-complete-icon">
                              <FaCheckCircle />
                            </div>
                            <div className="status-content">
                              <h4 className="status-value">{reauditStats.completed}</h4>
                              <p className="status-label">Completed</p>
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                    )}
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </div>

            <div className="dashboard-section progress-section">
              <DuserAuditProgress />
            </div>
          </div>
        )}
      </Container>

      <style>
        {`
          .dashboard-container {
            background-color: #f8f9fa;
            min-height: 100vh;
          }
          
          /* Updated styles for non-header design */
          .admin-avatar-simple {
            width: 45px;
            height: 45px;
            background-color: rgba(var(--bs-primary-rgb), 0.1);
            border: 2px solid rgba(var(--bs-primary-rgb), 0.2);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 16px;
            color: var(--bs-primary);
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          }
          
          .profile-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          
          .department-avatar-fallback {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 16px;
            color: var(--bs-primary);
          }
          
          .greeting {
            font-weight: 600;
            font-size: 1.25rem;
          }
          
          .dashboard-content {
            padding-bottom: 30px;
          }
          
          .dashboard-grid {
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: auto;
            gap: 15px;
          }
          
          .dashboard-section {
            background-color: transparent;
            border-radius: 12px;
          }
          
          .section-title {
            font-weight: 600;
            color: #344767;
            display: flex;
            align-items: center;
            font-size: 1rem;
          }
          
          .status-section {
            margin-bottom: 5px;
          }
          
          .status-tabs {
            border-bottom: 1px solid #e9ecef;
          }
          
          .status-tab {
            border: none;
            border-radius: 0;
            color: #6c757d;
            font-weight: 500;
            padding: 8px 16px;
            transition: all 0.2s ease;
          }
          
          .status-tab.active {
            color: var(--bs-primary);
            border-bottom: 2px solid var(--bs-primary);
            background: transparent;
          }
          
          .status-cards {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 15px;
            padding: 15px 0;
          }
          
          .status-card {
            border: none;
            border-radius: 12px;
            box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
            transition: all 0.3s ease;
            cursor: pointer;
          }
          
          .status-card .card-body {
            padding: 15px;
            display: flex;
            flex-direction: row;
            align-items: center;
          }
          
          .status-icon {
            width: 40px;
            height: 40px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
            margin-right: 12px;
            flex-shrink: 0;
          }
          
          .status-content {
            flex-grow: 1;
          }
          
          .status-value {
            font-weight: 700;
            font-size: 1.5rem;
            margin-bottom: 2px;
            line-height: 1.2;
          }
          
          .status-label {
            font-size: 0.8rem;
            font-weight: 500;
            color: #6c757d;
            margin: 0;
          }
          
          .progress-section {
            margin-bottom: 5px;
          }
          
          .pending-icon, .cap-pending-icon {
            background-color: rgba(255, 193, 7, 0.1);
            color: #ffc107;
          }
          
          .progress-icon, .cap-progress-icon {
            background-color: rgba(13, 110, 253, 0.1);
            color: #0d6efd;
          }
          
          .complete-icon, .cap-complete-icon {
            background-color: rgba(25, 135, 84, 0.1);
            color: #198754;
          }
          
          .status-card:hover {
            transform: translateY(-3px);
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
          }
          
          @media (min-width: 992px) {
            .dashboard-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              grid-template-areas:
                "status progress";
              gap: 20px;
              align-items: start;
            }
            
            .status-section {
              grid-area: status;
            }
            
            .progress-section {
              grid-area: progress;
              height: 100%;
              display: flex;
              flex-direction: column;
            }
            
            .status-cards {
              grid-template-columns: repeat(3, 1fr);
              gap: 10px;
            }
            
            .status-card .card-body {
              padding: 12px;
            }
            
            .status-value {
              font-size: 1.25rem;
            }
            
            .status-icon {
              width: 35px;
              height: 35px;
              font-size: 16px;
            }
          }
          
          @media (min-width: 1200px) {
            .dashboard-grid {
              grid-template-columns: 2fr 3fr;
              gap: 25px;
            }
            
            .status-cards {
              grid-template-columns: repeat(3, 1fr);
              gap: 12px;
            }
            
            .status-card .card-body {
              padding: 15px;
            }
            
            .status-value {
              font-size: 1.5rem;
            }
            
            .status-icon {
              width: 40px;
              height: 40px;
              font-size: 18px;
            }
          }
          
          @media (min-width: 1400px) {
            .dashboard-grid {
              grid-template-columns: 1fr 2fr;
            }
          }
          
          @media (max-width: 991px) {
            .dashboard-grid {
              grid-template-columns: 1fr;
              grid-template-areas:
                "status"
                "progress";
            }
            
            .status-section {
              grid-area: status;
            }
            
            .progress-section {
              grid-area: progress;
            }
          }
          
          @media (max-width: 576px) {
            .greeting {
              font-size: 1.1rem;
            }
            
            .status-value {
              font-size: 1.25rem;
            }
          }
        `}
      </style>
    </Container>
  );
}

export default InDuserDashboard;
